/**
 * VideoCallAdapter — contrato da camada de videochamada.
 *
 * Qualquer provedor (Daily.co, Twilio, Agora, Jitsi…) deve estender esta classe
 * e implementar todos os métodos. Nenhum componente Vue pode importar o SDK do
 * provedor diretamente — toda interação passa por aqui.
 *
 * Eventos semânticos (usados em on/off):
 *   'joining'            — usuário local iniciou conexão
 *   'joined'             — usuário local entrou na sala
 *   'participant-joined' — participante remoto entrou
 *   'participant-updated'— estado de participante mudou (áudio/vídeo/track)
 *   'participant-left'   — participante remoto saiu
 *   'message'            — mensagem de chat recebida
 *   'error'              — erro de conexão
 */
export default class VideoCallAdapter {
  /** Inicializa o call object com as opções da sala. Deve ser chamado antes de join(). */
  // eslint-disable-next-line no-unused-vars
  init(options) {
    throw new Error("VideoCallAdapter.init() not implemented");
  }

  /** Entra na sala. @param {string} userName @returns {Promise} */
  // eslint-disable-next-line no-unused-vars
  join(userName) {
    throw new Error("VideoCallAdapter.join() not implemented");
  }

  /** Sai da sala. @returns {Promise} */
  leave() {
    throw new Error("VideoCallAdapter.leave() not implemented");
  }

  /** Destrói o call object e libera todos os recursos de mídia. */
  destroy() {
    throw new Error("VideoCallAdapter.destroy() not implemented");
  }

  /** @returns {Array} lista de participantes (local e remotos) */
  getParticipants() {
    throw new Error("VideoCallAdapter.getParticipants() not implemented");
  }

  /** @returns {boolean} estado atual do áudio local */
  localAudio() {
    throw new Error("VideoCallAdapter.localAudio() not implemented");
  }

  /** @returns {boolean} estado atual do vídeo local */
  localVideo() {
    throw new Error("VideoCallAdapter.localVideo() not implemented");
  }

  /** @param {boolean} enabled */
  // eslint-disable-next-line no-unused-vars
  setLocalAudio(enabled) {
    throw new Error("VideoCallAdapter.setLocalAudio() not implemented");
  }

  /** @param {boolean} enabled */
  // eslint-disable-next-line no-unused-vars
  setLocalVideo(enabled) {
    throw new Error("VideoCallAdapter.setLocalVideo() not implemented");
  }

  /**
   * Envia mensagem de chat para todos os participantes da sala.
   * @param {{ body: string, senderName: string, senderType: string }} payload
   */
  // eslint-disable-next-line no-unused-vars
  sendMessage(payload) {
    throw new Error("VideoCallAdapter.sendMessage() not implemented");
  }

  /**
   * Registra handler para um evento semântico.
   * @param {string} event  — nome semântico (ver lista acima)
   * @param {Function} handler
   */
  // eslint-disable-next-line no-unused-vars
  on(event, handler) {
    throw new Error("VideoCallAdapter.on() not implemented");
  }

  /**
   * Remove handler de evento semântico.
   * @param {string} event
   * @param {Function} handler
   */
  // eslint-disable-next-line no-unused-vars
  off(event, handler) {
    throw new Error("VideoCallAdapter.off() not implemented");
  }
}
